import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { NotificationEvent } from '../models/notification-event.model';
import { ImportJobRealtimeEvent, toImportJobRealtimeEvent } from '../models/integration-data.model';
import { ProcessBatchDocumentType } from './ap-bulk-invoice-upload.service';

export interface ServiceRequestRealtimeEvent {
  serviceRequestId: string;
}

export interface ServiceRequestJobCompletedEvent {
  serviceRequestId: string;
  message: string;
  invoiceNumber?: string;
}

export interface ServiceRequestJobToast {
  type: 'success' | 'error';
  text: string;
}

/** BE change types sent over SSE when a System Admin changes an organization. */
export const ORGANIZATION_CHANGE_TYPES: readonly string[] = [
  'Module',
  'Sync',
  'Configuration',
  'OrganizationStatus',
  'Platform',
  'QuickBooksConnection',
  'DedicatedEmail',
  'Organization',
];

/**
 * SSE payload, e.g.
 * `{ "type": "organizationConfiguration", "companyId": "…", "change": "Sync", "key": "RepairAndMaintenanceToAccountsPayable" }`.
 */
export const ORGANIZATION_CONFIGURATION_EVENT_TYPE = 'organizationconfiguration';

export interface OrganizationChangedEvent {
  /** What changed: one of ORGANIZATION_CHANGE_TYPES. */
  change: string;
  companyId?: string;
  key?: string;
}

interface PendingBulkDraftUpload {
  documentType: ProcessBatchDocumentType;
  count: number;
}

@Injectable({ providedIn: 'root' })
export class AppRealtimeEventsService {
  private readonly serviceRequestCreatedSubject =
    new Subject<ServiceRequestRealtimeEvent>();

  private readonly serviceRequestJobCompletedSubject =
    new Subject<ServiceRequestJobCompletedEvent>();

  private readonly importJobSubject = new Subject<ImportJobRealtimeEvent>();

  private readonly organizationChangedSubject = new Subject<OrganizationChangedEvent>();

  private pendingBulkDraftUpload: PendingBulkDraftUpload | null = null;

  private readonly pendingExpenseIntegrateIds = new Set<string>();

  private readonly pendingApNetSuiteJobIds = new Set<string>();

  /** SSE payloads that include `serviceRequestId` without a job `message` (e.g. OCR draft created). */
  readonly serviceRequestCreated$: Observable<ServiceRequestRealtimeEvent> =
    this.serviceRequestCreatedSubject.asObservable();

  /** SSE payloads with `serviceRequestId` + `message` (e.g. post-approval NetSuite job). */
  readonly serviceRequestJobCompleted$: Observable<ServiceRequestJobCompletedEvent> =
    this.serviceRequestJobCompletedSubject.asObservable();

  /** SSE payloads with `type: "importJob"` (Integration data & import jobs). */
  readonly importJob$: Observable<ImportJobRealtimeEvent> = this.importJobSubject.asObservable();

  /** A System Admin changed the organization (modules, syncs, configurations, platform …): reload `/me`. */
  readonly organizationChanged$: Observable<OrganizationChangedEvent> =
    this.organizationChangedSubject.asObservable();

  /**
   * Whether an SSE payload is a notification count update. Import job and organization change events carry no
   * `count`; reading one as 0 would clear the bell badge.
   */
  isNotificationCountEvent(event: NotificationEvent): boolean {
    return !toImportJobRealtimeEvent(event) && !this.extractOrganizationChange(event);
  }

  publishNotificationEvent(event: NotificationEvent): void {
    const importJob = toImportJobRealtimeEvent(event);
    if (importJob) {
      this.importJobSubject.next(importJob);
      return;
    }

    const organizationChange = this.extractOrganizationChange(event);
    if (organizationChange) {
      this.organizationChangedSubject.next(organizationChange);
      return;
    }

    const normalized = this.normalizeEvent(event);
    const serviceRequestId = this.extractServiceRequestId(normalized);
    if (!serviceRequestId) {
      return;
    }

    const message = this.extractJobMessage(normalized);
    if (message) {
      this.serviceRequestJobCompletedSubject.next({
        serviceRequestId,
        message,
        invoiceNumber: this.extractInvoiceNumber(normalized),
      });
      return;
    }

    this.serviceRequestCreatedSubject.next({ serviceRequestId });
  }

  /** After bulk invoice upload API succeeds — wait for SSE before toast + list refresh. */
  markBulkDraftUploadPending(
    documentType: ProcessBatchDocumentType,
    count: number
  ): void {
    if (count <= 0) {
      return;
    }
    this.pendingBulkDraftUpload = { documentType, count };
  }

  /** After expense final approval — wait for backend integration job SSE. */
  markExpenseIntegratePending(serviceRequestId: string): void {
    const id = serviceRequestId?.trim();
    if (id) {
      this.pendingExpenseIntegrateIds.add(id);
    }
  }

  markExpenseIntegratePendingBatch(serviceRequestIds: string[]): void {
    serviceRequestIds.forEach((id) => this.markExpenseIntegratePending(id));
  }

  isExpenseIntegratePending(serviceRequestId: string): boolean {
    const id = serviceRequestId?.trim();
    return !!id && this.pendingExpenseIntegrateIds.has(id);
  }

  clearExpenseIntegratePending(serviceRequestId: string): void {
    const id = serviceRequestId?.trim();
    if (id) {
      this.pendingExpenseIntegrateIds.delete(id);
    }
  }

  /**
   * First SSE after expense approval integration — returns toast once, then clears pending.
   */
  acknowledgeExpenseIntegrateJob(
    event: ServiceRequestJobCompletedEvent
  ): ServiceRequestJobToast | null {
    if (this.pendingExpenseIntegrateIds.has(event.serviceRequestId)) {
      this.pendingExpenseIntegrateIds.delete(event.serviceRequestId);
      return this.buildExpenseIntegrateJobToast(event);
    }

    // Backend NetSuite job may broadcast the converted AP id, not the expense id.
    if (this.pendingExpenseIntegrateIds.size > 0) {
      const firstPending = this.pendingExpenseIntegrateIds.values().next().value;
      if (firstPending) {
        this.pendingExpenseIntegrateIds.delete(firstPending);
      }
      return this.buildExpenseIntegrateJobToast(event);
    }

    return null;
  }

  buildExpenseIntegrateJobToast(
    event: ServiceRequestJobCompletedEvent
  ): ServiceRequestJobToast {
    return this.buildJobCompletedToast(
      event,
      'Expense integrated successfully',
      'Expense integration failed',
      'Expense integration completed with an unknown status.'
    );
  }

  /** R&M → AP conversion job (after quotation approval with an advance, or invoice approval). */
  buildRmIntegrateJobToast(event: ServiceRequestJobCompletedEvent): ServiceRequestJobToast {
    // R&M events carry no invoice number (BE sends "NA"), so the toast is the message alone.
    return this.buildJobCompletedToast(
      { ...event, invoiceNumber: undefined },
      'Request integrated with Accounts Payable successfully',
      'Integration with Accounts Payable failed',
      'Integration with Accounts Payable completed with an unknown status.'
    );
  }

  /** After AP final approval — wait for backend NetSuite integration job SSE. */
  markApNetSuiteJobPending(serviceRequestId: string): void {
    const id = serviceRequestId?.trim();
    if (id) {
      this.pendingApNetSuiteJobIds.add(id);
    }
  }

  markApNetSuiteJobPendingBatch(serviceRequestIds: string[]): void {
    serviceRequestIds.forEach((id) => this.markApNetSuiteJobPending(id));
  }

  isApNetSuiteJobPending(serviceRequestId: string): boolean {
    const id = serviceRequestId?.trim();
    return !!id && this.pendingApNetSuiteJobIds.has(id);
  }

  clearApNetSuiteJobPending(serviceRequestId: string): void {
    const id = serviceRequestId?.trim();
    if (id) {
      this.pendingApNetSuiteJobIds.delete(id);
    }
  }

  acknowledgeApNetSuiteJob(
    event: ServiceRequestJobCompletedEvent
  ): ServiceRequestJobToast | null {
    if (this.pendingApNetSuiteJobIds.has(event.serviceRequestId)) {
      this.pendingApNetSuiteJobIds.delete(event.serviceRequestId);
      return this.buildApNetSuiteJobToast(event);
    }

    if (this.pendingApNetSuiteJobIds.size > 0) {
      const firstPending = this.pendingApNetSuiteJobIds.values().next().value;
      if (firstPending) {
        this.pendingApNetSuiteJobIds.delete(firstPending);
      }
      return this.buildApNetSuiteJobToast(event);
    }

    return null;
  }

  buildApNetSuiteJobToast(
    event: ServiceRequestJobCompletedEvent
  ): ServiceRequestJobToast {
    return this.buildJobCompletedToast(
      event,
      'Request integrated with NetSuite successfully',
      'NetSuite integration failed',
      'NetSuite integration completed with an unknown status.'
    );
  }

  /**
   * True when approval chain is complete, request is approved, and NetSuite is not yet submitted.
   */
  isApNetSuiteJobExpectedAfterApproval(
    details:
      | {
          status?: string;
          isNSSubmitted?: boolean;
          approvals?: Array<{ state?: string }>;
        }
      | null
      | undefined,
    approvedStatus: string,
    pendingApprovalState: string
  ): boolean {
    if (!details) {
      return false;
    }
    if (details.status !== approvedStatus) {
      return false;
    }
    if (details.isNSSubmitted) {
      return false;
    }
    const hasPendingApproval = (details.approvals || []).some(
      (item) => item.state === pendingApprovalState
    );
    return !hasPendingApproval;
  }

  private buildJobCompletedToast(
    event: ServiceRequestJobCompletedEvent,
    defaultSuccessPrefix: string,
    defaultFailurePrefix: string,
    unknownStatusMessage: string
  ): ServiceRequestJobToast {
    const invoiceSuffix = event.invoiceNumber
      ? ` (Invoice ${event.invoiceNumber})`
      : '';
    const message = (event.message || '').trim();
    const normalized = message.toLowerCase();

    if (this.isJobFailureMessage(normalized)) {
      const defaultFailure = `${defaultFailurePrefix}${invoiceSuffix}.`;
      const text = this.isBareJobFailureMessage(normalized)
        ? defaultFailure
        : message || defaultFailure;
      return {
        type: 'error',
        text,
      };
    }

    if (this.isJobSuccessMessage(normalized)) {
      const defaultSuccess = `${defaultSuccessPrefix}${invoiceSuffix}.`;
      const text =
        normalized === 'success' || normalized === 'succeeded'
          ? defaultSuccess
          : message || defaultSuccess;
      return { type: 'success', text };
    }

    return {
      type: 'error',
      text: message || unknownStatusMessage,
    };
  }

  private isJobSuccessMessage(normalized: string): boolean {
    if (!normalized) {
      return false;
    }
    if (normalized === 'success' || normalized === 'succeeded') {
      return true;
    }
    if (this.isJobFailureMessage(normalized)) {
      return false;
    }
    return /\bsuccess(ful(ly)?)?\b/.test(normalized);
  }

  private isJobFailureMessage(normalized: string): boolean {
    if (!normalized) {
      return false;
    }
    if (
      normalized === 'failed' ||
      normalized === 'failure' ||
      normalized === 'fail'
    ) {
      return true;
    }
    return /\b(fail(ed|ure)?|error)\b/.test(normalized);
  }

  /** BE sometimes sends only "failed" / "error" — replace with a user-facing sentence. */
  private isBareJobFailureMessage(normalized: string): boolean {
    if (!normalized) {
      return true;
    }
    return (
      normalized === 'failed' ||
      normalized === 'failure' ||
      normalized === 'fail' ||
      normalized === 'error'
    );
  }

  /**
   * First SSE after bulk upload — returns success toast message once, then clears pending.
   */
  acknowledgeBulkDraftUpload(): string | null {
    const pending = this.pendingBulkDraftUpload;
    if (!pending) {
      return null;
    }
    this.pendingBulkDraftUpload = null;
    return this.buildBulkDraftSuccessMessage(
      pending.documentType,
      pending.count
    );
  }

  private buildBulkDraftSuccessMessage(
    documentType: ProcessBatchDocumentType,
    succeeded: number
  ): string {
    if (documentType === 'expense') {
      return succeeded === 1
        ? 'Expense draft request created successfully'
        : 'Expense draft requests created successfully';
    }
    return succeeded === 1
      ? 'Account payable draft request created successfully'
      : `${succeeded} account payable draft requests created successfully`;
  }

  private extractOrganizationChange(event: NotificationEvent): OrganizationChangedEvent | null {
    const raw = (event ?? {}) as Record<string, unknown>;
    const text = (...keys: string[]): string => {
      const value = keys.map((key) => raw[key]).find((v) => typeof v === 'string');
      return typeof value === 'string' ? value.trim() : '';
    };

    const type = text('type', 'Type');
    const change = text('change', 'Change');
    const isOrganizationEvent =
      type.toLowerCase() === ORGANIZATION_CONFIGURATION_EVENT_TYPE || ORGANIZATION_CHANGE_TYPES.includes(change);
    if (!isOrganizationEvent) {
      return null;
    }

    return {
      change,
      companyId: text('companyId', 'CompanyId') || undefined,
      key: text('key', 'Key') || undefined,
    };
  }

  private normalizeEvent(event: NotificationEvent): NotificationEvent {
    const raw = event as Record<string, unknown>;
    return {
      ...event,
      serviceRequestId:
        (event.serviceRequestId as string | undefined) ??
        (raw['ServiceRequestId'] as string | undefined),
      message:
        (event.message as string | undefined) ??
        (raw['Message'] as string | undefined),
      invoiceNumber:
        (event.invoiceNumber as string | undefined) ??
        (raw['InvoiceNumber'] as string | undefined),
    };
  }

  private extractServiceRequestId(event: NotificationEvent): string | null {
    const raw = event?.serviceRequestId;
    if (typeof raw !== 'string') {
      return null;
    }
    const trimmed = raw.trim();
    return trimmed || null;
  }

  private extractJobMessage(event: NotificationEvent): string | null {
    const raw = event?.message;
    if (typeof raw !== 'string') {
      return null;
    }
    const trimmed = raw.trim();
    return trimmed || null;
  }

  private extractInvoiceNumber(event: NotificationEvent): string | undefined {
    const raw = event?.invoiceNumber;
    if (typeof raw !== 'string') {
      return undefined;
    }
    const trimmed = raw.trim();
    return trimmed || undefined;
  }
}
